import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { FaArrowRight, FaCalendarAlt, FaClock, FaUser } from 'react-icons/fa';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const blogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Taalsetu Blog — Learn Tabla, Indian Classical Music",
  "description": "Expert articles on learning tabla, Indian classical music, rhythms, and the art of tabla from Maestro Atharva Kagalkar and the Taalsetu team.",
  "url": "https://www.taalsetu.com/blog/",
  "publisher": {
    "@type": "MusicSchool",
    "@id": "https://www.taalsetu.com/#organization",
    "name": "Taalsetu Music Academy"
  }
};

const posts = [
  {
    slug: "learn-tabla-online-beginners",
    title: "How to Learn Tabla Online as a Complete Beginner",
    excerpt: "Everything you need to know to start learning tabla from scratch — equipment, practice routines, how to find the right teacher, and what to expect in your first 3 months.",
    date: "June 2, 2026",
    readTime: "8 min read",
    category: "Beginners Guide",
    tag: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  {
    slug: "intermediate-tabla-tips",
    title: "How to Improve Your Tabla Playing — Tips for Intermediate Students",
    excerpt: "Proven strategies for intermediate tabla players: rela technique, layakari drills, breaking through plateaus, and how to prepare for your first public performance.",
    date: "June 9, 2026",
    readTime: "9 min read",
    category: "Intermediate Guide",
    tag: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  },
  {
    slug: "tabla-classes-pune-atharva-kagalkar",
    title: "Tabla Classes in Pune with Maestro Atharva Kagalkar",
    excerpt: "Discover why students from across Pune and beyond choose Taalsetu Music Academy in Wakad for authentic tabla training. Learn about our teaching philosophy and class formats.",
    date: "June 2, 2026",
    readTime: "6 min read",
    category: "Pune & Local",
    tag: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  {
    slug: "online-tabla-classes-for-kids",
    title: "Online Tabla Classes for Kids — A Complete Parent's Guide",
    excerpt: "Is your child interested in Indian classical music? Find out the right age to start, what to expect, how to keep kids motivated, and why tabla is one of the best instruments for children.",
    date: "June 2, 2026",
    readTime: "7 min read",
    category: "Kids & Families",
    tag: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  },
];

const BlogIndex = () => {
  return (
    <div className="min-h-screen font-sans bg-white dark:bg-maroon-dark">
      <SEO
        title="Taalsetu Blog — Learn Tabla, Indian Classical Music Tips & Guides"
        description="Read expert guides on learning tabla online, tabla classes in Pune, tabla for kids, and Indian classical music. Written by Maestro Atharva Kagalkar and the Taalsetu team."
        keywords="tabla blog, learn tabla tips, tabla for beginners guide, tabla classes Pune, Indian classical music blog, tabla online guide, tabla kids, Atharva Kagalkar blog"
        url="https://www.taalsetu.com/blog/"
        schemaMarkup={blogSchema}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.taalsetu.com/' },
          { name: 'Blog', url: 'https://www.taalsetu.com/blog/' }
        ]}
      />

      {/* Header */}
      <motion.section
        className="relative pt-28 pb-20 bg-gradient-to-br from-maroon via-maroon to-maroon-dark text-white overflow-hidden"
        initial="hidden" animate="visible" variants={fadeUp}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-10 w-48 h-48 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="font-serif text-4xl md:text-6xl font-bold mb-6">
            Taalsetu <span className="text-gold">Blog</span>
          </h1>
          <div className="gold-divider mb-6" />
          <p className="text-lg md:text-xl text-gold-light/75 max-w-2xl mx-auto">
            Expert guides on learning tabla, Indian classical music, and everything in between — from Maestro Atharva Kagalkar and the Taalsetu team.
          </p>
        </div>
      </motion.section>

      {/* Blog Posts */}
      <motion.section
        className="py-20"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {posts.map((post, i) => (
              <motion.article
                key={i} variants={fadeUp}
                className="group bg-white dark:bg-maroon-dark border border-gold/10 rounded-2xl p-8 hover:border-gold/30 hover:shadow-xl transition-all duration-400"
              >
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${post.tag}`}>
                    {post.category}
                  </span>
                  <div className="flex items-center gap-4 text-xs text-maroon/40 dark:text-gold-light/40">
                    <span className="flex items-center gap-1"><FaCalendarAlt />{post.date}</span>
                    <span className="flex items-center gap-1"><FaClock />{post.readTime}</span>
                    <span className="flex items-center gap-1"><FaUser />Atharva Kagalkar</span>
                  </div>
                </div>
                <h2 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-3 group-hover:text-gold transition-colors duration-300">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h2>
                <p className="text-maroon/65 dark:text-gold-light/65 leading-relaxed mb-6">{post.excerpt}</p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all duration-300"
                >
                  Read Article <FaArrowRight className="text-sm" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-16 bg-maroon/5 dark:bg-maroon-dark/50 border-t border-gold/10"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-4">
            Ready to Start Playing Tabla?
          </h2>
          <p className="text-maroon/60 dark:text-gold-light/60 mb-6">
            Explore our beginner, intermediate, and advanced courses — and book your first trial class today.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/courses/beginner/" className="btn-gold !rounded-xl flex items-center px-8 py-3">
              View Courses <FaArrowRight className="ml-2" />
            </Link>
            <Link to="/register" className="px-8 py-3 border border-gold/40 text-maroon dark:text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300">
              Register Free
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default BlogIndex;
